import { ReactElement, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';

/**
 * Higher-order component for observing and consuming changes in element dimensions.
 *
 * @param {Object} props JSX attributes passed to component.
 * @param {Function} props.callback callback to be executed on dimension change.
 * @param {Node} props.children element to be observed. Element identified by `id` attribute.
 * @returns {ReactElement} rendered component.
 */
const ResizeObserverHOC = ({ callback, children }) => {
	const refObserver = useRef(null);

	/**
	 * Helper method for lazily creating and retrieving memoized {@link ResizeObserver} instance.
	 * @returns {ResizeObserver} {@link ResizeObserver} instance.
	 */
	const getObserver = () => {
		if (refObserver?.current === null) {
			refObserver.current = new ResizeObserver(callback);
		}
		return refObserver.current;
	};

	/**
	 * Hook on props change. Configures observation of children element.
	 * @returns {Function} callback disconnecting all active element observations.
	 */
	useEffect(() => {
		const observer = getObserver();

		const element = children?.props?.id
			? document.getElementById(children.props.id)
			: null;

		if (element) {
			// unobserve previous element if children changed
			observer.disconnect();
			observer.observe(element);
		}

		return () => observer.disconnect();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [children]);

	return children;
};

ResizeObserverHOC.propTypes = {
	callback: PropTypes.func.isRequired,
	children: PropTypes.node.isRequired,
};

export { ResizeObserverHOC };
